import React from "react";

/**
 * URL regex — matches http(s), www., and plain domains followed by path.
 * Trailing punctuation (.,;:!?) is stripped from the captured URL so a sentence
 * like "see https://x.com." doesn't include the period in the link.
 */
const URL_RE = /(https?:\/\/[^\s<>()]+|www\.[^\s<>()]+)/gi;
const TRAILING_PUNCT = /[.,;:!?\])'"]+$/;

/**
 * Split a free-text string into a React fragment with `<a>` tags around any
 * URL-shaped tokens. Used in PDF renderers so the print → save-as-PDF flow
 * preserves clickable links, and the rasterized export can collect their
 * bounding rects.
 */
export function linkify(text, className = "") {
  if (!text || typeof text !== "string") return text || null;
  const out = [];
  let lastIdx = 0;
  let match;
  URL_RE.lastIndex = 0;
  while ((match = URL_RE.exec(text)) !== null) {
    const start = match.index;
    let raw = match[0];
    let trail = "";
    const tm = raw.match(TRAILING_PUNCT);
    if (tm) {
      trail = tm[0];
      raw = raw.slice(0, raw.length - trail.length);
    }
    if (start > lastIdx) out.push(text.slice(lastIdx, start));
    const href = raw.startsWith("http") ? raw : `https://${raw}`;
    out.push(
      <a
        key={`${start}-${raw}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className || "text-[var(--tir-burgundy)] underline break-all"}
      >
        {raw}
      </a>
    );
    if (trail) out.push(trail);
    lastIdx = start + match[0].length;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out.length ? <>{out}</> : text;
}

/**
 * Best-effort hostname for rendering an audio/external link with a short label
 * instead of the full URL. Falls back to the raw value.
 */
export function shortLinkLabel(url) {
  if (!url) return "";
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, "") + (u.pathname && u.pathname !== "/" ? u.pathname : "");
  } catch {
    return url;
  }
}
